import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Users, Target, TrendingUp, Loader2, Award } from 'lucide-react';
import { ope } from '../../../services/api';
import { useToast } from '../../../context/ToastContext';

const OpeAdminRetoStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const { error } = useToast();

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            setLoading(true);
            const data = await ope.adminGetRetoStats();
            setStats(data);
        } catch (err) {
            console.error('Error loading reto stats:', err);
            error('Error al cargar las estadísticas del reto');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
        );
    }

    if (!stats) return null;

    const { global, tendencia, topUsuarios, distribucionAciertos } = stats;

    return (
        <div className="p-6 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-b-xl space-y-8">
            
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                    icon={TrendingUp} 
                    label="Participaciones Totales" 
                    value={global.totalParticipaciones} 
                    color="text-blue-400"
                />
                <StatCard 
                    icon={Users} 
                    label="Usuarios Únicos" 
                    value={global.usuariosUnicos} 
                    color="text-purple-400"
                />
                <StatCard 
                    icon={Target} 
                    label="Precisión Media" 
                    value={`${global.precisionMedia}%`} 
                    color="text-green-400"
                />
                <StatCard 
                    icon={Award} 
                    label="Puntos Repartidos" 
                    value={global.totalPuntosRepartidos} 
                    color="text-yellow-400"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Tendencia de participación */}
                <div className="bg-black/20 p-4 rounded-xl border border-[var(--glass-border)]">
                    <h4 className="text-sm font-medium text-gray-400 mb-6 uppercase tracking-wider">Tendencia (Últimos 14 días)</h4>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={tendencia}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis 
                                    dataKey="fecha" 
                                    tick={{ fill: '#9CA3AF', fontSize: 10 }} 
                                    tickFormatter={(str) => new Date(str).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                                />
                                <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                                <Tooltip 
                                    contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                                    itemStyle={{ color: '#F3F4F6' }}
                                />
                                <Line type="monotone" dataKey="participaciones" name="Participaciones" stroke="#6366F1" strokeWidth={2} dot={{ r: 4 }} />
                                <Line type="monotone" dataKey="usuarios" name="Usuarios" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Distribución de aciertos */}
                <div className="bg-black/20 p-4 rounded-xl border border-[var(--glass-border)]">
                    <h4 className="text-sm font-medium text-gray-400 mb-6 uppercase tracking-wider">Distribución de Aciertos</h4>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={distribucionAciertos}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="aciertos" name="Nº Aciertos" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                                <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                                <Tooltip 
                                    contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                                    cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                />
                                <Bar dataKey="cantidad" name="Frecuencia" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Ranking de Engagement */}
            <div className="bg-black/20 p-4 rounded-xl border border-[var(--glass-border)]">
                <h4 className="text-sm font-medium text-gray-400 mb-4 uppercase tracking-wider">Top 5 Usuarios más Comprometidos</h4>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-700">
                                <th className="py-2 font-medium text-gray-400">Usuario</th>
                                <th className="py-2 font-medium text-gray-400 text-center">Días</th>
                                <th className="py-2 font-medium text-gray-400 text-center">Puntos</th>
                                <th className="py-2 font-medium text-gray-400 text-right">Precisión</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topUsuarios.map((u, i) => (
                                <tr key={u.id} className="border-b border-gray-800/50 hover:bg-white/5 transition-colors">
                                    <td className="py-3 flex items-center gap-2">
                                        <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${i === 0 ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-300'}`}>
                                            {i + 1}
                                        </span>
                                        {u.nombre}
                                    </td>
                                    <td className="py-3 text-center">{u.diasParticipados}</td>
                                    <td className="py-3 text-center font-semibold text-yellow-400">{u.puntosTotales}</td>
                                    <td className="py-3 text-right text-green-400">{parseFloat(u.precisionMedia).toFixed(1)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white/5 p-4 rounded-xl border border-[var(--glass-border)] flex items-center gap-4">
        <div className={`p-3 rounded-lg bg-black/40 ${color}`}>
            <Icon className="w-5 h-5" />
        </div>
        <div>
            <p className="text-xs text-gray-500 font-medium">{label}</p>
            <h4 className="text-xl font-bold text-[var(--text-primary)]">{value}</h4>
        </div>
    </div>
);

export default OpeAdminRetoStats;
